<template>
  <div class="finding-detail">
    <div v-if="!resolvedFinding" class="finding-empty">
      <div class="text-h6 mb-2">Finding not found</div>
      <p class="text-body-2 text-medium-emphasis mb-4">
        This diagnosis finding is unavailable, or it is behind the Action Center preview.
      </p>
      <button type="button" class="finding-back" @click="goBack">
        <v-icon size="18">mdi-arrow-left</v-icon>
        Back to Action center
      </button>
    </div>

    <template v-else>
      <div class="finding-topbar">
        <button type="button" class="finding-back" @click="goBack">
          <v-icon size="18">mdi-arrow-left</v-icon>
          Action center
          <span class="finding-crumb">/ Finding detail</span>
        </button>
        <div class="finding-stepper" aria-label="Diagnosis steps">
          <span class="step step-done"><v-icon size="14">mdi-check</v-icon> 1 · Diagnosis</span>
          <span class="step step-active">2 · Details</span>
          <button
            v-if="solutionAgentAvailable && !solutionNothingToRepair"
            type="button"
            class="step step-link"
            @click="openSolution"
          >3 · Solution</button>
          <span v-else-if="solutionAgentAvailable" class="step">3 · Solution</span>
          <span v-else class="step step-soon">3 · Solution <em>Soon</em></span>
        </div>
      </div>

      <section class="finding-header">
        <div class="finding-header-main">
          <div class="brand-eyebrow">
            {{ finding.code || 'FINDING' }} —
            {{ (finding.severity || 'unknown').toUpperCase() }} SEVERITY,
            {{ (finding.fix_effort || 'unknown').toUpperCase() }} EFFORT
          </div>
          <h1 class="brand-heading finding-title">{{ finding.title }}</h1>
          <div class="finding-chip-row">
            <v-chip size="small" :color="severityColor(finding.severity)" variant="tonal">
              {{ normalizeLabel(finding.severity) }}
            </v-chip>
            <v-chip size="small" variant="outlined">
              {{ normalizeLabel(finding.fix_effort) }} effort
            </v-chip>
            <span class="meta-chip">Gap type · {{ gapCategoryLabel(finding) }}</span>
            <span v-if="finding.source_of_issue" class="meta-chip">
              Source · {{ finding.source_of_issue }}
            </span>
          </div>
        </div>
        <div v-if="confidencePct != null" class="confidence-box">
          <div class="confidence-value">{{ confidencePct }}%</div>
          <div class="confidence-label">Confidence</div>
        </div>
      </section>

      <div class="finding-grid">
        <div class="finding-main">
          <section class="finding-card">
            <div class="finding-section-label">The diagnosis</div>
            <p class="finding-prose">{{ finding.description }}</p>
          </section>

          <section class="finding-card">
            <div class="finding-section-label">Why it's happening</div>
            <p class="finding-prose">{{ finding.why_it_exists }}</p>
            <div v-if="finding.root_cause_category" class="root-cause-row">
              <span class="root-cause-label">Root cause</span>
              <span class="root-cause-chip">
                <span class="root-cause-dot"></span>
                {{ normalizeLabel(finding.root_cause_category) }}
              </span>
            </div>
          </section>

          <section class="finding-card">
            <div class="finding-section-head">
              <div class="finding-section-label">Supporting evidence</div>
              <span class="meta-chip">{{ evidenceItems.length }} {{ evidenceItems.length === 1 ? 'source' : 'sources' }}</span>
            </div>
            <div v-if="evidenceItems.length" class="evidence-list">
              <div v-for="(item, i) in evidenceItems" :key="i" class="evidence-item">
                <v-icon size="16" class="mr-2 text-medium-emphasis">mdi-link-variant</v-icon>
                <a v-if="item.href" :href="item.href" target="_blank" rel="noopener" class="finding-url">{{ item.label }}</a>
                <span v-else>{{ item.label }}</span>
                <span v-if="item.note" class="evidence-note"> — {{ item.note }}</span>
              </div>
            </div>
            <p v-else class="finding-prose text-medium-emphasis">No supporting evidence listed.</p>
          </section>

          <DiagnosisGapTypeBand :finding="finding" />

          <section class="finding-json">
            <div class="finding-section-head">
              <div>
                <div class="finding-section-label">Raw diagnosis JSON — temporary</div>
                <p class="json-note">Kept here while we confirm every field is mapped.</p>
              </div>
              <div class="d-flex align-center ga-2">
                <button type="button" class="json-toggle" @click="showRawJson = !showRawJson">
                  {{ showRawJson ? 'Hide raw diagnosis JSON' : 'View raw diagnosis JSON' }}
                  <v-icon size="16">{{ showRawJson ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </button>
                <button v-if="showRawJson" type="button" class="copy-json-btn" @click="copyJson">
                  {{ copied ? 'Copied' : 'Copy JSON' }}
                </button>
              </div>
            </div>
            <pre v-if="showRawJson" class="json-block">{{ prettyJson }}</pre>
          </section>
        </div>

        <aside class="finding-rail">
          <section class="finding-card">
            <div class="finding-section-label">This keeps showing up</div>
            <div class="recur-pill">{{ affectedQueries.length }} of {{ totalQueryCount }}</div>
            <p class="finding-prose">
              This gap affects {{ affectedQueries.length }} of {{ totalQueryCount }} queries in this report.
            </p>
            <div class="affected-query-list">
              <div
                v-for="query in affectedQueries"
                :key="`rail-${query.id}-${query.text}`"
                class="affected-query-item"
              >
                <v-icon size="14" class="mr-2 text-medium-emphasis">mdi-magnify</v-icon>
                <span>{{ query.text || `Query ${query.id}` }}</span>
              </div>
            </div>
          </section>

          <section class="finding-card">
            <div class="finding-section-label">Report context</div>
            <div class="report-context">
              <div class="brand-avatar">{{ brandInitials(reviewEntity?.brand_name) }}</div>
              <div>
                <div class="report-brand">{{ reviewEntity?.brand_name }}</div>
                <div class="report-meta">
                  {{ [reviewEntity?.product_name, reviewEntity?.region].filter(Boolean).join(' · ') }}
                </div>
              </div>
            </div>
            <div v-if="confidencePct != null" class="report-confidence">
              Finding confidence {{ confidencePct }}%
            </div>
          </section>

          <section v-if="isEvidenceInsufficient" class="finding-card finding-card-dashed">
            <div class="finding-section-label">Not enough signal to recommend a fix</div>
            <p class="finding-prose">Investigate the gaps listed in the main column before a fix can be recommended.</p>
          </section>
          <section v-else class="finding-card">
            <div class="finding-section-label">Recommended fix</div>
            <h2 v-if="fixTitle" class="fix-title">{{ fixTitle }}</h2>
            <p v-if="fixDescription" class="finding-prose">{{ fixDescription }}</p>
            <div v-if="fixTags.length" class="chip-row">
              <span v-for="tag in fixTags" :key="tag" class="fix-tag">{{ tag }}</span>
            </div>
            <div v-if="fixLinks.length" class="url-stack mt-2">
              <a
                v-for="link in fixLinks"
                :key="link"
                :href="link"
                target="_blank"
                rel="noopener"
                class="finding-url"
              >{{ link }}</a>
            </div>
            <div v-if="solutionAgentAvailable" class="solution-launch">
              <div class="solution-title">Schema solution agent</div>
              <p>{{ solutionCopy }}</p>
              <v-btn
                block
                color="primary"
                variant="flat"
                rounded="pill"
                class="text-none font-weight-bold"
                :loading="startingSolution"
                :disabled="solutionNothingToRepair"
                @click="openSolution"
              >{{ solutionCtaLabel }}</v-btn>
              <div class="solution-footnote">{{ solutionFootnote }}</div>
            </div>
            <div v-else class="solution-slot">
              <div class="solution-title">Solution Agent — coming soon</div>
              <p>One click to draft and apply the fix will live here.</p>
            </div>
          </section>
        </aside>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from '@/axios.js'
import DiagnosisGapTypeBand from './DiagnosisGapTypeBand.vue'
import {
  affectedQueriesFor,
  agentFindingPayload,
  asList,
  brandInitials,
  confidencePercent,
  gapCategoryLabel,
  isFindingDetailAccessible,
  isHttpUrl,
  isRecommendedFieldKey,
  normalizeFindings,
  normalizeLabel,
  schemaPagesToShow,
  severityColor,
  sortFindingsByPriority,
  withFindingKeys
} from '@/utils/diagnosisFindings'
import { isTerminalRunStatus } from '@/utils/solutionSchema'

const BASE_URL = process.env.VUE_APP_API_BASE_URL

export default {
  name: 'DiagnosisFindingDetail',
  components: { DiagnosisGapTypeBand },
  props: {
    findingId: {
      type: [String, Number],
      required: true
    },
    diagnosisFindings: {
      type: Array,
      default: () => []
    },
    diagnosisPreview: {
      type: Object,
      default: null
    },
    queries: {
      type: Array,
      default: () => []
    },
    reviewEntity: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      showRawJson: false,
      copied: false,
      startingSolution: false,
      existingSolutionStatus: null,
      solutionFetchId: 0
    }
  },
  computed: {
    ...mapGetters(['lacksIntelligencePlan', 'isFreeTierPlan']),
    solutionPageCount() {
      return schemaPagesToShow(this.finding).length
    },
    // Visibility plans get the solution for the schema gap in their preview only; Free never does.
    solutionAgentAvailable() {
      const finding = this.resolvedFinding
      if (!finding) return false
      if (String(finding.code || '').toUpperCase() !== 'SCHEMA_GAP') return false
      if (String(finding.diagnosis_status || '').toLowerCase() !== 'done') return false
      if (!this.lacksIntelligencePlan) return true
      if (this.isFreeTierPlan) return false
      const findingId = String(finding.id || finding.key)
      // Two readable findings can both be schema gaps; only the one the server pins may run.
      const schemaFindingId = this.diagnosisPreview?.schema_finding_id
      if (schemaFindingId !== undefined) return schemaFindingId !== null && String(schemaFindingId) === findingId
      const readableIds = this.diagnosisPreview?.finding_ids
      return Array.isArray(readableIds) && readableIds.map(String).includes(findingId)
    },
    solutionReportId() {
      return this.solutionAgentAvailable ? this.resolvedFinding.diagnosis_report_id : null
    },
    solutionGenerated() {
      return this.existingSolutionStatus === 'done'
    },
    solutionInProgress() {
      return Boolean(this.existingSolutionStatus) && !isTerminalRunStatus(this.existingSolutionStatus)
    },
    solutionNothingToRepair() {
      return this.existingSolutionStatus === 'nothing_to_repair'
    },
    solutionCtaLabel() {
      if (this.solutionNothingToRepair) return 'Nothing to repair'
      if (this.solutionGenerated) return 'Show the generated schema →'
      if (this.solutionInProgress) return 'See the run in progress →'
      const pages = this.solutionPageCount
      if (!pages) return 'Generate schema for this finding →'
      return `Generate schema for ${pages === 1 ? 'this page' : `all ${pages} pages`} →`
    },
    solutionCopy() {
      if (this.solutionNothingToRepair) return 'The agent already checked these pages and found no schema gap left to repair. Re-run the diagnosis if the pages have changed.'
      if (this.solutionGenerated) return 'The agent already wrote the block for each affected page. Open it to review and paste.'
      if (this.solutionInProgress) return 'The agent is reading these pages now. Open the run to watch it land.'
      return 'It reads each page, checks what your markup says today, and writes the block for you to paste.'
    },
    solutionFootnote() {
      if (this.solutionNothingToRepair) return 'Checked earlier for this finding'
      if (this.solutionGenerated) return 'Generated earlier for this finding'
      return 'One run, one block per affected page'
    },
    allFindings() {
      if (Array.isArray(this.diagnosisFindings) && this.diagnosisFindings.length > 0) {
        return withFindingKeys(this.diagnosisFindings)
      }
      return (this.queries || []).flatMap((query) => {
        const report = query?.diagnosis_report
        if (!report || String(report.status || '').toLowerCase() !== 'done') return []
        return normalizeFindings(report.findings).map((finding, index) => ({
          ...finding,
          id: finding.id || `${query.id}-${finding.code || index}`,
          key: `${query.id}-${finding.code || index}`,
          query_id: query.id,
          query_text: query.query_text
        }))
      })
    },
    prioritizedFindings() {
      return sortFindingsByPriority(this.allFindings)
    },
    finding() {
      const id = String(this.findingId)
      return this.allFindings.find((item) => String(item.id) === id || String(item.key) === id) || null
    },
    resolvedFinding() {
      if (!this.finding) return null
      if (!isFindingDetailAccessible(
        this.lacksIntelligencePlan, this.diagnosisPreview, this.prioritizedFindings, this.finding
      )) {
        return null
      }
      return this.finding
    },
    confidencePct() {
      return confidencePercent(this.finding)
    },
    evidenceItems() {
      return asList(this.finding?.supporting_evidence).map((item) => {
        if (typeof item === 'string') {
          const [left, ...rest] = item.split(' — ')
          const href = isHttpUrl(left) ? left.trim() : (isHttpUrl(item) ? item.trim() : null)
          return { href, label: left, note: rest.join(' — ') || null }
        }
        return {
          href: item?.url || null,
          label: item?.title || item?.url || JSON.stringify(item),
          note: item?.note || null
        }
      })
    },
    affectedQueries() {
      return affectedQueriesFor(this.finding, this.allFindings)
    },
    totalQueryCount() {
      const fromQueries = Array.isArray(this.queries) ? this.queries.length : 0
      if (fromQueries > 0) return fromQueries
      return new Set(this.allFindings.map((f) => f.query_id).filter(Boolean)).size
    },
    isEvidenceInsufficient() {
      return String(this.finding?.code || '').toUpperCase() === 'EVIDENCE_INSUFFICIENT'
    },
    recommendedEntries() {
      return Object.entries(this.finding || {}).filter(([key, value]) => {
        if (!isRecommendedFieldKey(key)) return false
        return asList(value).length > 0
      })
    },
    fixTitle() {
      const pageType = this.finding?.recommended_page_type || this.finding?.recommended_faq_format
      if (pageType) return `Publish a ${normalizeLabel(pageType)}`
      const first = this.recommendedEntries[0]
      if (!first) return 'Recommended next step'
      const values = asList(first[1]).filter((item) => typeof item === 'string' && !isHttpUrl(item))
      if (values[0] && values[0].length > 24) return values[0]
      return normalizeLabel(first[0].replace(/^recommended_/, ''))
    },
    fixDescription() {
      return null
    },
    fixTags() {
      const tags = []
      this.recommendedEntries.forEach(([, value]) => {
        asList(value).forEach((item) => {
          if (typeof item === 'string' && !isHttpUrl(item) && item.length <= 48) tags.push(item)
        })
      })
      return [...new Set(tags)].slice(0, 6)
    },
    fixLinks() {
      const links = []
      this.recommendedEntries.forEach(([, value]) => {
        asList(value).forEach((item) => {
          if (isHttpUrl(item)) links.push(item)
          if (item?.url && isHttpUrl(item.url)) links.push(item.url)
        })
      })
      return [...new Set(links)].slice(0, 6)
    },
    prettyJson() {
      return JSON.stringify(agentFindingPayload(this.finding), null, 2)
    }
  },
  watch: {
    solutionReportId: {
      immediate: true,
      handler() {
        this.loadExistingSolution()
      }
    }
  },
  methods: {
    async loadExistingSolution() {
      this.existingSolutionStatus = null
      const fetchId = (this.solutionFetchId += 1)
      if (!this.solutionReportId) return

      try {
        const response = await axios.get(
          `${BASE_URL}/diagnosis_reports/${this.solutionReportId}/solution_runs/latest`,
          {
            params: { gap_code: 'SCHEMA_GAP' },
            headers: { Authorization: localStorage.getItem('auth_token') }
          }
        )
        if (fetchId !== this.solutionFetchId) return
        const run = response.data?.data?.solution_run
        this.existingSolutionStatus = run ? String(run.status || '').toLowerCase() : null
      } catch (error) {
        console.error('Error loading existing schema solution:', error)
      }
    },
    brandInitials,
    gapCategoryLabel,
    normalizeLabel,
    severityColor,
    goBack() {
      const query = { ...this.$route.query }
      delete query.finding_id
      delete query.step
      this.$router.replace({ path: '/analytics', query })
    },
    openSolution() {
      if (!this.solutionAgentAvailable || this.solutionNothingToRepair || this.startingSolution) return
      this.startingSolution = true
      const query = { ...this.$route.query, step: 'solution' }
      this.$router.push({ path: '/analytics', query }).catch(() => {}).finally(() => {
        this.startingSolution = false
      })
    },
    async copyJson() {
      try {
        await navigator.clipboard.writeText(this.prettyJson)
        this.copied = true
        setTimeout(() => { this.copied = false }, 1600)
      } catch (error) {
        console.error('Failed to copy diagnosis JSON', error)
      }
    }
  }
}
</script>

<style scoped>
.finding-detail {
  padding: 8px 8px 48px;
}

.finding-empty {
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 16px;
  padding: 32px;
  text-align: center;
}

.finding-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.finding-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: var(--brand-ocean-600);
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
}

.finding-crumb {
  color: var(--brand-ink-800);
  font-weight: 500;
}

.finding-stepper {
  display: flex;
  gap: 14px;
  font-size: 12px;
  font-weight: 600;
}

.step {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--brand-ink-800);
}

.step-done {
  color: #127a6a;
}

.step-active {
  color: var(--brand-ocean-600);
}

.step-soon {
  color: #8496bc;
}

.step-soon em {
  font-style: normal;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-left: 4px;
}

.finding-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 16px;
  padding: 22px 24px;
  margin-bottom: 16px;
}

.finding-title {
  font-size: 26px;
  line-height: 1.25;
  margin: 8px 0 14px;
}

.finding-chip-row,
.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  background: var(--brand-sand-100);
  color: var(--brand-ink-800);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.confidence-box {
  flex-shrink: 0;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 8px solid var(--brand-ocean-100);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
}

.confidence-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--brand-teal-500);
  line-height: 1;
}

.confidence-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--brand-ink-800);
  margin-top: 4px;
}

.finding-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 16px;
  align-items: start;
}

.finding-main,
.finding-rail {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.finding-card {
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 14px;
  padding: 18px 20px;
}

.finding-card-dashed {
  border-style: dashed;
}

.finding-section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--brand-ink-800);
  margin-bottom: 10px;
}

.finding-section-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}

.finding-prose {
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: var(--brand-ink-950);
}

.root-cause-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.root-cause-label {
  font-size: 11px;
  font-weight: 700;
  color: var(--brand-ink-800);
}

.root-cause-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--brand-amber-100);
  color: #92610a;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.root-cause-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--brand-amber-400);
}

.evidence-list,
.affected-query-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.evidence-item,
.affected-query-item {
  display: flex;
  align-items: flex-start;
  font-size: 13px;
  color: var(--brand-ink-800);
  line-height: 1.4;
}

.finding-url {
  color: var(--brand-ocean-600);
  word-break: break-all;
}

.evidence-note {
  color: var(--brand-ink-800);
}

.affected-query-list {
  margin-top: 10px;
  border: 1px solid var(--brand-sand-300);
  border-radius: 10px;
  background: var(--brand-sand-50);
  padding: 8px;
}

.recur-pill {
  display: inline-flex;
  background: var(--brand-ocean-100);
  color: var(--brand-ocean-600);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 8px;
}

.report-context {
  display: flex;
  gap: 12px;
  align-items: center;
}

.brand-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--brand-ocean-100);
  color: var(--brand-ocean-600);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
}

.report-brand {
  font-weight: 700;
}

.report-meta,
.report-confidence,
.json-note {
  font-size: 12px;
  color: var(--brand-ink-800);
}

.report-confidence {
  margin-top: 10px;
}

.fix-title {
  font-family: Lora, serif;
  font-size: 18px;
  margin: 0 0 8px;
}

.fix-tag {
  background: var(--brand-ocean-100);
  color: var(--brand-ocean-600);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.step-link {
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  color: var(--brand-ocean-600);
  font: inherit;
  font-weight: 600;
}

.solution-launch {
  margin-top: 16px;
  border: 1px solid var(--brand-sand-300);
  border-radius: 12px;
  padding: 14px;
  background: var(--brand-sand-50);
}

.solution-launch p {
  margin: 0 0 12px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--brand-ink-800);
}

.solution-footnote {
  margin-top: 8px;
  font-size: 11.5px;
  color: #8496bc;
  text-align: center;
}

.solution-slot {
  margin-top: 16px;
  border: 1px dashed var(--brand-sand-300);
  border-radius: 12px;
  padding: 12px;
  background: var(--brand-sand-50);
}

.solution-title {
  font-weight: 700;
  font-size: 13px;
  margin-bottom: 4px;
}

.solution-slot p {
  margin: 0;
  font-size: 12px;
  color: var(--brand-ink-800);
}

.finding-json {
  background: #fff;
  border: 1px solid var(--brand-sand-300);
  border-radius: 14px;
  padding: 18px 20px;
}

.json-toggle,
.copy-json-btn {
  border: 1px solid var(--brand-sand-300);
  background: #fff;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: var(--brand-ocean-600);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.json-block {
  margin: 12px 0 0;
  padding: 16px;
  background: var(--brand-ink-950);
  color: #d6e4f5;
  border-radius: 10px;
  overflow: auto;
  font-size: 12px;
  line-height: 1.5;
  max-height: 480px;
}

.url-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

@media (max-width: 1100px) {
  .finding-grid {
    grid-template-columns: 1fr;
  }

  .confidence-box {
    width: 96px;
    height: 96px;
  }
}
</style>
